/**
 * Pure helpers for the ticket-stub avatar stack. We deterministically pick
 * faces from the in-app avatar set based on a seed (e.g. the brand name) so a
 * given squad always shows the same stack across renders — no Math.random in
 * the render path, no flicker on re-mount.
 */

import { AVATAR_IDS, type AvatarId } from '../../../constants/avatarIds';
import { getAvatarAsset } from '../../../src/avatars/helpers/getAvatarAsset';
import type { AvatarGroupItem } from '../../shared/AvatarGroup/AvatarGroup';

const ALL_AVATAR_IDS: AvatarId[] = Object.values(AVATAR_IDS);

/** Cheap deterministic 32-bit hash (FNV-ish). Stable across platforms. */
function seedHash(seed: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h ^ seed.charCodeAt(i)) >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/**
 * Pick `count` avatar IDs deterministically from `seed`. Result is unique
 * within a single call when `count <= ALL_AVATAR_IDS.length` (which it always
 * is — we only ever ask for 3).
 */
export function pickAvatarIds(seed: string, count: number): AvatarId[] {
  if (count <= 0) return [];
  const pool = [...ALL_AVATAR_IDS];
  const base = seedHash(seed || 'default');
  const out: AvatarId[] = [];
  for (let i = 0; i < count && pool.length > 0; i++) {
    const idx = (base + i * 2654435761) % pool.length;
    out.push(pool.splice(idx, 1)[0]);
  }
  return out;
}

/**
 * Build the AvatarGroup item list for the ticket-stub stack. `visibleCount`
 * is the number of faces to draw (we always cap at memberCount so the +N chip
 * remains correct).
 */
export function buildTicketStubAvatarItems(
  seed: string,
  memberCount: number,
  visibleCount: number,
): AvatarGroupItem[] {
  const faces = Math.max(0, Math.min(memberCount, visibleCount));
  if (faces === 0) return [];
  return pickAvatarIds(seed, faces).map<AvatarGroupItem>((id) => ({
    source: getAvatarAsset(id, 'idle', true),
    label: id,
  }));
}
